import { prepareSortingParams } from 'api/common-schemas';
import {
  OrdersByLocationAPIResponse,
  CalendarEventsResponse,
  OrdersByLocationSearchParams,
  OrdersByLocationSearchParamsSchema,
  OrdersIndexAPIResponse,
  ResponseActiveOrdersGraphType,
  RecentUpdateItem,
} from './schemas';
import { apiClient } from 'api/apiClient';
import { RebillServiceDetails } from 'screens/Inventory/NonGRTInvoices/RebillServiceDetailsModal/schemas';

type CalendarEventsParams =
  | { from_date: string; to_date: string }
  | { month: number; year: number };

export interface IUpdateItem {
  id: string;
  title: string;
  time: string;
  date: string;
  address1: string;
  address2: string;
  subtitle: string;
  tag: {
    label: string;
  };
}

export const getActiveOrdersGraph =
  async (): Promise<ResponseActiveOrdersGraphType> => {
    const response = await apiClient.get('/api/v1/orders/tracking/counts');
    return response.data;
  };

const mapOrderLocationColumns = {
  address_order: 'location',
  percentage_order: 'status',
  site_name_order: 'site_name',
  num_services_order: 'num_services',
};

export const getOrdersByLocation = async (
  args: OrdersByLocationSearchParams,
): Promise<OrdersByLocationAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    args,
    mapOrderLocationColumns,
    OrdersByLocationSearchParamsSchema,
    { sortByKey: 'sort_by', shouldUppercaseOrder: false },
  );

  const response = await apiClient.get('/api/v1/orders/tracking/locations', {
    params: {
      ...newQueryParams,
    },
  });
  return {
    ...response.data,
    metadata: {
      ...response.data.metadata,
      total_items:
        (response.data?.metadata?.total_items ||
          response.data?.metadata?.total) ??
        0,
    },
  };
};

const mapOrderColumns = {
  address_order: 'location',
  g360_status_order: 'status',
  site_name_order: 'site_name',
  order_number_order: 'order_number',
  product_name_order: 'product_name',
  date_ordered_order: 'date_ordered',
  date_confirmed_order: 'date_confirmed',
};

export const getOrdersByOrder = async (
  args: OrdersByLocationSearchParams,
): Promise<OrdersIndexAPIResponse> => {
  const newQueryParams = prepareSortingParams(
    args,
    mapOrderColumns,
    OrdersByLocationSearchParamsSchema,
    { sortByKey: 'sort_by', shouldUppercaseOrder: false },
  );

  const response = await apiClient.get('/api/v1/orders/tracking/orders', {
    params: { ...newQueryParams },
  });
  return {
    ...response.data,
    metadata: {
      ...response.data.metadata,
      total_items:
        (response.data?.metadata?.total_items ||
          response.data?.metadata?.total) ??
        0,
    },
  };
};

export const getRecentUpdates = async (
  params: string[],
): Promise<RecentUpdateItem[]> => {
  const response = await apiClient.get(
    '/api/v1/orders/tracking/recent_updates',
    {
      params: { filter_type: params.join(',') },
    },
  );
  return response.data;
};

export const getExportOrdersAsCVS = async (
  args: Omit<OrdersByLocationSearchParams, 'page' | 'page_size'>,
): Promise<Blob> => {
  const newQueryParams = prepareSortingParams(
    args,
    mapOrderColumns,
    OrdersByLocationSearchParamsSchema,
    { sortByKey: 'sort_by', shouldUppercaseOrder: false },
  );

  const response = await apiClient.get(
    '/api/v1/orders/tracking/orders/download',
    {
      params: { ...newQueryParams },
      responseType: 'blob',
    },
  );
  return response.data;
};

export const getCalendarEvents = async (
  params: CalendarEventsParams,
): Promise<CalendarEventsResponse> => {
  const response = await apiClient.get('/api/v1/orders/tracking/calendar', {
    params,
  });
  return response.data;
};

export const uploadInvoice = async (data: FormData) => {
  const response = await apiClient.post(
    `/api/v1/orders/invoices/upload`,
    data,
    {
      headers: {
        'Content-type': 'multipart/form-data',
      },
    },
  );
  return response.data;
};

export const getInvoiceDetails = async (uploadId: string) => {
  const response = await apiClient.get(`/api/v1/orders/invoices/${uploadId}`);
  return response.data;
};

export const processInvoice = async (uploadId: string) => {
  const response = await apiClient.post(`/api/v1/orders/invoices/process`, {
    upload_id: uploadId,
  });
  return response.data;
};

export const getInvoiceComparison = async (quoteId: string) => {
  const response = await apiClient.get(
    `/api/v1/orders/invoices/compare/${quoteId}`,
  );
  return response.data;
};

export const postInvoiceRebill = (
  invoiceId: string,
  data: RebillServiceDetails,
) => {
  const payload = buildServicePayload(data);

  return apiClient.post(`/api/v1/orders/invoices/${invoiceId}/rebill`, payload);
};

export const buildServicePayload = (data: RebillServiceDetails) => {
  return {
    locations: [
      {
        address: {
          ...data.site,
          address1: data.site?.address_line_1 || '',
          address2: data.site?.address_line_2 || '',
          city: data.site?.city || '',
          state: data.site?.state || '',
          zip: data.site?.zip || '',
          business_name: data.business_name || '',
          unit: data.unit || '',
          billing_type: data.billing_type?.value ?? null,
          invoice_style: data.invoice_style?.value ?? null,
          invoice_delivery_method: data.invoice_delivery_method?.value ?? null,
          ...(!data.site?.macnum
            ? {
                parent_macnum: data.parent_account?.value,
                parent_name: data.parent_account?.label,
              }
            : {}),
        },
        source: 'REBILL',
        services: data.services.map((svc) => {
          let normalizedSpeed: string | undefined;

          if (svc.bandwidth) {
            const num = Number(svc.bandwidth.amount);
            const unit = svc.bandwidth.unit.value;
            normalizedSpeed =
              unit === 'Gbps' ? String(num * 1000) : String(num);
          }
          const base = {
            type: svc.service_type.value,
            identifier: svc.service_identifier || '',
            vendor_name: data.vendor_name,
            vendor_account_number: data.vendor_account_number,
            mrc: typeof data.mrc === 'string' ? parseFloat(data.mrc) : data.mrc,
            contract_start_date: data.contract_start_date,
            contract_end_date: data.contract_end_date,
            extra_details: {} as Record<string, string | undefined | null>,
          };

          switch (svc.service_type.value) {
            case 'Broadband':
            case 'DIA':
            case 'PIP/GSE':
              base.extra_details = {
                subtype: svc.subtype,
                data_speed: normalizedSpeed,
              };
              break;

            case 'Mobility':
              base.extra_details = {
                data_speed: normalizedSpeed,
              };
              break;

            case 'DID':
            case 'POTS':
            case 'RCF':
            case 'SIP':
            case 'Toll-Free':
            case 'LD':
            case 'Hosted PBX/EPOTS':
              base.identifier = svc.btn || '';
              base.extra_details = Object.fromEntries(
                (svc.wtns || []).map((wtn, i) => [`wtn_${i + 1}`, wtn]),
              );
              break;

            case 'Television':
              break;
          }

          return base;
        }),
      },
    ],
  };
};
